import React, { useState, useEffect, useMemo, useRef, Suspense } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import { signOut } from 'firebase/auth';
import { limpiarStorageSensibleAlSalir } from '../utils/limpiarSesion';
import { registrarActividad } from '../services/actividadService';
import { BigQueryService } from '../services/bigQueryService';
import '../pages/Dashboard.css';
import BurbujaNotificaciones from '../components/notificaciones/BurbujaNotificaciones';
import Modal from '../components/Modal';
import AvisoActualizacion from '../components/ui/AvisoActualizacion';
import SinAccesoModulo from '../components/ui/SinAccesoModulo';
import { CargandoModulo, LimiteErrorChunk, lazyConReintento } from '../components/ui/CargaModulos';
import aviso from '../utils/aviso';
import { MENU_SECTIONS_COMPLETO, getModuloInfo } from './menuConfig';
import { moduloDeLaRuta, estadoDelCandado, MODULOS_PARA_TODOS } from './accesoModulos';
import {
  XMarkIcon, HomeIcon, SpinnerIcon, ExclamationTriangleIcon,
  Bars3Icon, UserCircleIcon, ChevronDownIcon, ChevronRightIcon, ChevronUpIcon,
  ChevronDoubleLeftIcon, ChevronDoubleRightIcon,
  ArrowRightOnRectangleIcon, KeyIcon, PlusIcon, SparklesIcon, LightModeIcon, DarkModeIcon
} from '../components/ui/Icons';

// Niveles de Asistente IA ya disponibles (el icono se muestra solo para estos). Potente desde el 02/10/2026: el
// servidor igual exige rol admin + Potente encendido en asistente_config/main; si no, contesta como Básico.
// Intermedio desde el 08/10/2026 (plan §6): el servidor exige el Intermedio encendido en Ajustes; si no, contesta como Básico.
const NIVELES_IA_DISPONIBLES = ['basico', 'intermedio', 'potente'];

// Lumi (el panel del asistente) se carga diferido, recién al primer clic del botón: su código no viaja en el arranque
// del ERP. Antes se importaba directo y arrastraba el Manual de Uso entero (~645,000 caracteres del archivo principal,
// medido en el build del 01/10/2026). Plan: docs/planes/ASISTENTE_IA_SISTEMA_PLAN.md.
const AsistenteIA = lazyConReintento(() => import('../components/asistente/AsistenteIA'));

// Si el panel de Lumi no se puede cargar (típico: salió una versión nueva y el archivo viejo ya no existe), se avisa con
// un toast y se desmonta, para que el próximo clic lo vuelva a intentar. El guardián general (LimiteErrorChunk)
// dibujaría su aviso grande al final de la página, lejos del botón (revisor, 01/10/2026).
class LimiteErrorLumi extends React.Component {
  constructor(props) {
    super(props);
    this.state = { fallo: false };
  }

  static getDerivedStateFromError() {
    return { fallo: true };
  }

  componentDidCatch(error) {
    console.error('No se pudo cargar Lumi:', error);
    aviso.error('No se pudo abrir Lumi. Actualizá la página e intentá de nuevo.');
    if (this.props.onFallo) this.props.onFallo();
  }

  render() {
    return this.state.fallo ? null : this.props.children;
  }
}




function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Al cambiar de módulo (ruta), resetear el scroll arriba. El contenedor que
  // scrollea es `.content-area` (overflow-y:auto), no la ventana. Global: aplica
  // a TODOS los módulos, no hay que tocar cada uno.
  useEffect(() => {
    const el = document.querySelector('.content-area');
    if (el) el.scrollTop = 0;
  }, [location.pathname]);
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [permisosUsuario, setPermisosUsuario] = useState(null);
  const [usuarioActual, setUsuarioActual] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [seccionesAbiertas, setSeccionesAbiertas] = useState({});
  const [tipoMenu, setTipoMenu] = useState('estatico');
  // Accesos rápidos: array de IDs de módulos en orden (máx 10 desktop, primeros 5 en mobile).
  // Arranca desde el cache localStorage (si existe) para que aparezcan al instante;
  // null = sin cache y Firestore todavía no responde (no se muestra nada en la barra).
  const [accesosRapidos, setAccesosRapidos] = useState(() => {
    try {
      const email = auth.currentUser?.email;
      if (!email) return null;
      const raw = localStorage.getItem(`accesos_rapidos_${email}`);
      if (!raw) return null;
      const ids = JSON.parse(raw);
      return Array.isArray(ids) ? ids : null;
    } catch (e) {
      return null;
    }
  });
  // Modal global de alerta del token Meta (≤7 días para vencer + user es destinatario)
  const [alertaTokenMeta, setAlertaTokenMeta] = useState(null); // null = no aparece, {} = mostrar
  // Aviso emergente de reposición de stock (null = no aparece; array = productos por comprar)
  const [alertaReposicion, setAlertaReposicion] = useState(null);
  const [expandidoReposicion, setExpandidoReposicion] = useState(false);
  // Panel de Lumi, el Asistente IA (guía de uso del sistema). Se monta al primer clic y queda montado.
  const [asistenteAbierto, setAsistenteAbierto] = useState(false);
  const [asistenteMontado, setAsistenteMontado] = useState(false);
  // Nivel de Asistente IA del usuario (explícito, NO se hereda de admin/'todos')
  const [nivelAsistenteIA, setNivelAsistenteIA] = useState('ninguno');
  // Tema claro/oscuro. El script de index.html ya aplicó el guardado antes del render (sin parpadeo).
  const [tema, setTema] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'claro'
  );
  const toggleTema = () => {
    const nuevo = tema === 'dark' ? 'claro' : 'dark';
    setTema(nuevo);
    if (nuevo === 'dark') document.documentElement.setAttribute('data-theme', 'dark');
    else document.documentElement.removeAttribute('data-theme');
    try { localStorage.setItem('tema', nuevo); } catch (e) { /* sin localStorage: igual funciona la sesión */ }
  };

  // Cargar permisos del usuario
  useEffect(() => {
    const cargarPermisosUsuario = async () => {
      try {
        const userEmail = auth.currentUser?.email;
        if (!userEmail) return;

        const { collection, getDocs, query, where, doc, getDoc } = await import('firebase/firestore');
        const usuariosQuery = query(collection(db, 'usuarios'), where('email', '==', userEmail));
        const usuariosSnapshot = await getDocs(usuariosQuery);
        
        if (!usuariosSnapshot.empty) {
          const usuarioDoc = usuariosSnapshot.docs[0];
          const usuarioData = usuarioDoc.data();
          setUsuarioActual({
            id: usuarioDoc.id,
            ...usuarioData
          });
          // Accesos rápidos del usuario (array de IDs de módulos, en orden)
          const accesosFirestore = Array.isArray(usuarioData.accesosRapidos) ? usuarioData.accesosRapidos : [];
          setAccesosRapidos(accesosFirestore);
          // Actualizar el cache para que la próxima carga los muestre al instante
          try {
            localStorage.setItem(`accesos_rapidos_${userEmail}`, JSON.stringify(accesosFirestore));
          } catch (e) { /* localStorage no disponible: sin cache, igual funciona */ }

          // Cargar preferencia de menú
          setTipoMenu(usuarioData.tipoMenuPreferido || 'estatico');

          // Si es menú desplegable, inicializar todas las secciones cerradas
          if (usuarioData.tipoMenuPreferido === 'desplegable') {
            setSeccionesAbiertas({});
          }

          // Leer permisos SIEMPRE (también admins) para el nivel de Asistente IA,
          // que es explícito y NO se hereda de ser admin / 'todos'.
          const permisosDoc = await getDoc(doc(db, 'permisos_usuarios', usuarioDoc.id));
          const permisosData = permisosDoc.exists() ? permisosDoc.data() : {};
          setNivelAsistenteIA(permisosData.nivelAsistenteIA || 'ninguno');

          if (usuarioData.rol === 'admin') {
            setPermisosUsuario('todos');
            return;
          }

          setPermisosUsuario(Array.isArray(permisosData.modulos) ? permisosData.modulos : []);
        } else {
          // Sesión sin ficha en `usuarios`: sin permisos, igual que si la lectura falla. Antes los permisos quedaban en
          // null para siempre ("Cargando permisos..."); con el candado de ruta, cada módulo quedaría cargando sin fin.
          setPermisosUsuario([]);
        }
      } catch (error) {
        console.error('Error cargando permisos:', error);
        setPermisosUsuario([]);
      }
    };

    cargarPermisosUsuario();
  }, []);

  // Aplicar en vivo el cambio de "Tipo de Menú" hecho desde Mi Perfil (sin recargar la app).
  useEffect(() => {
    const handler = (e) => {
      const nuevo = e.detail || 'estatico';
      setTipoMenu(nuevo);
      if (nuevo === 'desplegable') setSeccionesAbiertas({});
    };
    window.addEventListener('menuPreferenceChanged', handler);
    return () => window.removeEventListener('menuPreferenceChanged', handler);
  }, []);

  // Mi Perfil avisa cuando el colaborador cambia su preferencia del botón "Marcar asistencia"
  // en la computadora, para que Inicio la aplique EN VIVO (usuarioActual se carga una sola vez).
  useEffect(() => {
    const handler = (e) => {
      setUsuarioActual(prev => (prev ? { ...prev, marcaAsistenciaEscritorio: e.detail === true } : prev));
    };
    window.addEventListener('marcaEscritorioChanged', handler);
    return () => window.removeEventListener('marcaEscritorioChanged', handler);
  }, []);

  // El botón "Abrir en Lumi" de la campanita del resumen de la mañana (BurbujaNotificaciones → irALumi, 23/10/2026): abre
  // el panel de Lumi en esa conversación. Solo si la persona tiene Lumi; lo que ve lo decide igual el servidor.
  const nivelAsistenteRef = useRef('ninguno');
  nivelAsistenteRef.current = nivelAsistenteIA;
  const [conversacionPedida, setConversacionPedida] = useState(null);
  useEffect(() => {
    const handler = (e) => {
      const id = e && e.detail && typeof e.detail.id === 'string' ? e.detail.id : '';
      if (!id || !NIVELES_IA_DISPONIBLES.includes(nivelAsistenteRef.current)) return;
      setConversacionPedida({ id, vez: Date.now() });
      setAsistenteMontado(true);
      setAsistenteAbierto(true);
    };
    window.addEventListener('lumi:abrir-conversacion', handler);
    return () => window.removeEventListener('lumi:abrir-conversacion', handler);
  }, []);

  // Lumi por WhatsApp (tanda 3, 29/10/2026; docs/planes/LUMI_WHATSAPP_PLAN.md §7, punto 3): el enlace "la respuesta completa
  // está en el ERP" trae `?lumi=<conversación>`. Se abre igual que el botón de la campanita, apenas se sabe que la persona
  // tiene Lumi, y se quita de la dirección (que atrás o recargar no la vuelva a abrir). Lo que ve lo decide el servidor:
  // solo sus conversaciones.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('lumi');
    if (!id || !/^[A-Za-z0-9]{10,40}$/.test(id) || !NIVELES_IA_DISPONIBLES.includes(nivelAsistenteIA)) return;
    params.delete('lumi');
    const resto = params.toString();
    navigate({ pathname: location.pathname, search: resto ? `?${resto}` : '' }, { replace: true });
    window.dispatchEvent(new CustomEvent('lumi:abrir-conversacion', { detail: { id } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, nivelAsistenteIA]);

  // Chequeo de alerta del token Meta — corre 1 vez al montar Layout (login, refresh, nueva pestaña).
  // Usa sessionStorage para no volver a aparecer dentro de la misma sesión si el user lo cerró.
  // Si user no es destinatario o días > 7, la CF devuelve mostrarAlerta=false y no hace nada.
  useEffect(() => {
    if (!usuarioActual?.email) return;

    // sessionStorage flag: si en esta misma sesión (tab) el user ya cerró el modal, no mostrar otra vez
    const cerradoEnEstaSesion = sessionStorage.getItem('alertaTokenMeta_cerrado');
    if (cerradoEnEstaSesion === '1') return;

    let cancelado = false;
    (async () => {
      try {
        const resultado = await BigQueryService.verificarAlertaTokenMeta(usuarioActual.email);
        if (cancelado) return;
        if (resultado && resultado.mostrarAlerta) {
          setAlertaTokenMeta(resultado);
        }
      } catch (e) {
        console.error('Error verificando alerta token Meta:', e);
      }
    })();
    return () => { cancelado = true; };
  }, [usuarioActual?.email]);

  // Aviso emergente de REPOSICIÓN DE STOCK — les salta a las personas elegidas en el
  // módulo (lista aparte del permiso del widget), cada N horas configurables.
  //
  // 💰 Se lee SIEMPRE la configuración (1 documento por carga, centavos al año) y el
  // reloj de localStorage decide si toca mostrar. 🔴 El reloj NO puede mirarse antes de
  // leer la configuración: si no, agregarte como destinatario no se nota hasta que venza
  // (pasó de verdad — se marcaba el reloj al ver "no me toca" y el cambio quedaba dormido
  // 6 horas). Por eso, además, un cambio de configuración POSTERIOR al último aviso
  // reabre el reloj: lo que se acaba de configurar se ve al refrescar.
  // A la Cloud Function solo se le pregunta cuando de verdad corresponde avisar.
  useEffect(() => {
    if (!usuarioActual?.email) return;
    let cancelado = false;
    (async () => {
      try {
        // Firestore se carga bajo demanda, igual que en el resto de Layout (code-splitting).
        const { doc, getDoc } = await import('firebase/firestore');
        const cfgSnap = await getDoc(doc(db, 'configuracion', 'alertas_reposicion'));
        if (cancelado) return;
        const cfg = cfgSnap.exists() ? (cfgSnap.data() || {}) : {};
        const destinatarios = Array.isArray(cfg.destinatarios) ? cfg.destinatarios : [];
        const esDestinatario = destinatarios
          .map((e) => String(e).toLowerCase())
          .includes(usuarioActual.email.toLowerCase());
        if (!esDestinatario) return;      // no le toca: sin marcar nada, sin consultar nada

        const vistoMs = Number(localStorage.getItem('alertaReposicion_vistoMs') || 0);
        const horas = Number(cfg.horasPopup) || 6;
        const configCambioDespues = Number(cfg.actualizadoMs || 0) > vistoMs;
        if (!configCambioDespues && Date.now() - vistoMs < horas * 3600000) return;  // todavía no toca

        const res = await BigQueryService.evaluarAlertasReposicion(false);
        if (cancelado) return;
        const porComprar = (res.items || []).filter((i) => i.bajo && !i.silenciado);
        if (porComprar.length > 0) setAlertaReposicion(porComprar);
        else localStorage.setItem('alertaReposicion_vistoMs', String(Date.now()));
      } catch (e) {
        console.error('Error verificando alertas de reposición:', e);
      }
    })();
    return () => { cancelado = true; };
  }, [usuarioActual?.email]);

  // Las dos salidas del aviso marcan el reloj: vuelve a aparecer recién en N horas.
  const posponerReposicion = () => {
    localStorage.setItem('alertaReposicion_vistoMs', String(Date.now()));
    setAlertaReposicion(null);
  };
  const irAReposicion = () => {
    posponerReposicion();
    navigate('/alertas-reposicion');
  };

  // Handlers del modal
  const irARenovarToken = () => {
    sessionStorage.setItem('alertaTokenMeta_cerrado', '1');
    setAlertaTokenMeta(null);
    navigate('/ads-dashboard?renovar=1');
  };
  const cerrarAlertaTokenMeta = () => {
    sessionStorage.setItem('alertaTokenMeta_cerrado', '1');
    setAlertaTokenMeta(null);
  };

  // Cerrar menú móvil al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen) {
        const sidebar = document.querySelector('.sidebar');
        const mobileToggle = document.querySelector('.mobile-menu-toggle');
        
        if (sidebar && !sidebar.contains(event.target) && 
            mobileToggle && !mobileToggle.contains(event.target)) {
          setMobileMenuOpen(false);
        }
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    try {
      // Auditoría: registrar ANTES de signOut (el token aún es válido). El helper
      // usa keepalive, así que el request sobrevive al cierre de sesión.
      await registrarActividad('sesion', 'logout', 'Cerró sesión');
      limpiarStorageSensibleAlSalir(); // A5: no dejar datos financieros/catálogos en equipos compartidos
      await signOut(auth);
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  // Lista completa de módulos viene de pages/menuConfig.js (single source of truth)
  const menuSections = permisosUsuario === null ? [] :
    permisosUsuario === 'todos' ? MENU_SECTIONS_COMPLETO :
    MENU_SECTIONS_COMPLETO.map(section => ({
      ...section,
      items: section.items.filter(item => {
        // Manual de Uso, Mi Asistencia y Mis Cursos: para todos (cada quien ve lo suyo)
        if (MODULOS_PARA_TODOS.includes(item.id)) return true;
        if (usuarioActual?.rol === 'admin' && (item.id === 'usuarios' || item.id === 'permisos')) {
          return true;
        }
        return permisosUsuario.includes(item.id);
      })
    })).filter(section => section.items.length > 0);
    
    const toggleSeccion = (seccionTitle) => {
    // Cada sección abre/cierra de forma independiente: varias pueden quedar abiertas a la vez.
    setSeccionesAbiertas(prev => ({
      ...prev,
      [seccionTitle]: !prev[seccionTitle]
    }));
  };

  // Click en el título de sección: alterna su estado (sin tocar las demás) y, al abrir, si la
  // sección queda fuera de la vista baja lo justo para mostrarla.
  const manejarClickSeccion = (e, seccionTitle) => {
    if (tipoMenu !== 'desplegable') return;
    const seEstaAbriendo = !seccionesAbiertas[seccionTitle];
    // ¿Ya había alguna sección abierta? Si NO, al abrir esta aparece el botón "Contraer
    // todo" arriba (empuja el menú hacia abajo) y en ese caso NO se compensa con scroll,
    // porque sino la primera sección se esconde detrás del botón.
    const habiaAlgunaAbierta = Object.values(seccionesAbiertas).some(Boolean);
    const tituloEl = e.currentTarget;                       // div .menu-section-title
    const seccionEl = tituloEl.closest('.menu-section');
    const container = tituloEl.closest('.menu-container');
    const topAntes = tituloEl.getBoundingClientRect().top;  // posición del título ANTES del cambio
    toggleSeccion(seccionTitle);
    if (!seEstaAbriendo || !container || !seccionEl) return;
    requestAnimationFrame(() => {
      // 1. ANCLAR: por si algo arriba cambió de alto, mantengo el título donde se tocó —
      //    antes del paint, sin parpadeo. Solo si el botón "Contraer todo" YA existía:
      //    si recién aparece al abrir la primera sección, su empuje es deseado (no se compensa).
      if (habiaAlgunaAbierta) {
        const topDespues = tituloEl.getBoundingClientRect().top;
        container.scrollTop += (topDespues - topAntes);
      }
      // 2. REVELAR: si la sección entra en la vista, bajo lo justo para mostrar sus ítems
      //    (secciones del final). Si es más alta que la vista, el anclaje ya la dejó bien.
      const items = seccionEl.querySelectorAll('.menu-item');
      const ultimo = items[items.length - 1];
      if (ultimo && seccionEl.offsetHeight <= container.clientHeight) {
        ultimo.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  };

  const getCurrentDate = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString('es-ES', options);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Al cambiar de módulo (por menú, acceso rápido o navegación directa): abrir la sección
  // del módulo activo (acordeón) y posicionar la vista del menú en el ítem marcado.
  useEffect(() => {
    if (permisosUsuario === null) return;
    const seccionActiva = menuSections.find(sec => sec.items.some(it => it.path === location.pathname));
    if (seccionActiva && tipoMenu === 'desplegable') {
      setSeccionesAbiertas(prev => prev[seccionActiva.title] ? prev : { ...prev, [seccionActiva.title]: true });
    }
    const t = setTimeout(() => {
      const activo = document.querySelector('.sidebar .menu-item.active');
      if (activo) activo.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 120);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, tipoMenu, permisosUsuario]);

  // Accesos rápidos resueltos a {id, label, Icon, path}. Filtrados por permisos del usuario
  // (por si el user ya no tiene permiso a un módulo que tenía configurado).
  const accesosRapidosResueltos = (() => {
    if (accesosRapidos === null) return []; // ni cache ni Firestore todavía
    return accesosRapidos
      .map(id => getModuloInfo(id))
      .filter(Boolean)
      .filter(info => {
        // Permisos aún cargando pero hay cache: mostrar lo cacheado (las rutas
        // están protegidas igual; al llegar los permisos se re-filtra solo)
        if (permisosUsuario === null) return true;
        if (permisosUsuario === 'todos') return true;
        if (MODULOS_PARA_TODOS.includes(info.id)) return true;
        if (usuarioActual?.rol === 'admin' && (info.id === 'usuarios' || info.id === 'permisos')) return true;
        return permisosUsuario.includes(info.id);
      });
  })();

  // 🔒 Candado de ruta (09/10/2026): un módulo del menú que la persona no tiene en Permisos no se abre escribiendo su
  // dirección ni con un enlace; en su lugar va "No tenés acceso a este módulo" y el módulo no se monta. Decide la
  // MISMA lista que dibuja el menú lateral (menuSections); el admin abre todo sin esperar la lectura de permisos.
  // Es la capa de la pantalla: los datos los cuidan las reglas y las funciones. Detalle en pages/accesoModulos.js.
  const esAdminSesion = permisosUsuario === 'todos' || user?.rol === 'admin';
  const visibleEnMenu = (id) => menuSections.some((seccion) => seccion.items.some((it) => it.id === id));
  const candadoDe = (moduloId) =>
    estadoDelCandado({ moduloId, permisosUsuario, esAdmin: esAdminSesion, visibleEnMenu });
  const moduloDeEstaRuta = useMemo(() => moduloDeLaRuta(location.pathname), [location.pathname]);
  const candadoRuta = candadoDe(moduloDeEstaRuta);
  // Para los botones y enlaces que llevan a OTRO módulo (widgets de Inicio, campanita, Revisar Pedidos IA…): se
  // muestran solo si el candado dejaría abrirlo. Llega a las pantallas por el contexto del <Outlet>. Recibe el id
  // del módulo como está en menuConfig.js; mientras se leen los permisos, devuelve false.
  const puedeAbrirModulo = (moduloId) => candadoDe(moduloId) === 'abrir';

  const accesosRapidosDesktop = accesosRapidosResueltos.slice(0, 10);
  const accesosRapidosMobile = accesosRapidosResueltos.slice(0, 5);
  // true cuando hay cache local o ya respondió Firestore — evita el flash de
  // "Agregá tus accesos" antes de saber si el usuario tiene o no accesos configurados
  const accesosCargados = accesosRapidos !== null || permisosUsuario !== null;

  return (
    <div className="dashboard-container">
      <div className={`sidebar ${menuCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
            {/* Ambos logos siempre en el DOM: el tema los alterna por CSS (instantáneo,
                sin bajar otra imagen al cambiar claro/oscuro). */}
            <img
              src="/logos/paralellum-negro.png"
              alt="PARALELLUM"
              className="sidebar-logo sidebar-logo--claro"
              onClick={() => navigate('/')}
              style={{ cursor: 'pointer' }}
              onError={(e) => {
                e.target.style.display = 'none';
                document.getElementById('sidebar-logo-text').style.display = 'block';
              }}
            />
            <img
              src="/logos/paralellum-blanco.png"
              alt="PARALELLUM"
              className="sidebar-logo sidebar-logo--oscuro"
              onClick={() => navigate('/')}
              style={{ cursor: 'pointer' }}
              onError={(e) => {
                e.target.style.display = 'none';
                document.getElementById('sidebar-logo-text').style.display = 'block';
              }}
            />
            <h3
              id="sidebar-logo-text"
              style={{display: 'none', cursor: 'pointer'}}
              onClick={() => navigate('/')}
            >
              PARALELLUM
            </h3>
            <button
              onClick={() => navigate('/')}
              style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '5px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.2s' }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--color-borde-suave)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Ir a Inicio"
            >
              <HomeIcon className="w-6 h-6" />
            </button>
          </div>
          {!menuCollapsed && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '6px' }}>
              <button
                onClick={toggleTema}
                role="switch"
                aria-checked={tema === 'dark'}
                aria-label={tema === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
                title={tema === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
                style={{
                  position: 'relative', width: '60px', height: '30px', flexShrink: 0,
                  padding: 0, cursor: 'pointer', borderRadius: '999px',
                  border: '1px solid var(--color-borde)',
                  background: tema === 'dark' ? 'rgba(124,107,218,0.45)' : 'var(--color-gris-borde)',
                  transition: 'background 0.3s ease'
                }}
              >
                <span style={{
                  position: 'absolute', top: '2px', left: tema === 'dark' ? '32px' : '2px',
                  width: '24px', height: '24px', borderRadius: '50%', background: '#ffffff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: tema === 'dark' ? '#5B45B0' : '#E0A82E',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
                  transition: 'left 0.3s cubic-bezier(0.34,1.56,0.64,1), color 0.3s ease'
                }}>
                  {tema === 'dark' ? <DarkModeIcon className="w-3 h-3" /> : <LightModeIcon className="w-3 h-3" />}
                </span>
              </button>
            </div>
          )}
        </div>

        {tipoMenu === 'desplegable' && !menuCollapsed && Object.values(seccionesAbiertas).some(Boolean) && (
          <div className="menu-colapsar-wrap">
            <button
              className="menu-colapsar-todo btn-press hover-bright"
              onClick={() => setSeccionesAbiertas({})}
              title="Contraer todas las secciones"
            >
              <ChevronUpIcon className="w-4 h-4" /> Contraer todo
            </button>
          </div>
        )}

        <div className="menu-container">
          {permisosUsuario === null ? (
            <div style={{padding: '20px', textAlign: 'center', color: '#666'}}>
              <span style={{display:'inline-flex', alignItems:'center', gap:'6px'}}><span style={{color:'#aaa', display:'inline-flex'}}><SpinnerIcon className="w-4 h-4" /></span> Cargando permisos...</span>
            </div>
          ) : menuSections.length === 0 ? (
            <div style={{padding: '20px', textAlign: 'center', color: '#666'}}>
              <span style={{display:'inline-flex', alignItems:'center', gap:'6px'}}><span style={{color:'#856404', display:'inline-flex'}}><ExclamationTriangleIcon className="w-4 h-4" /></span> Sin permisos asignados</span>
              <br/>
              <small>Contacta al administrador</small>
            </div>
          ) : (
            menuSections.map((section, index) => (
              <div key={index} className="menu-section">
                <div
                  className="menu-section-title"
                  onClick={(e) => manejarClickSeccion(e, section.title)}
                  style={{
                    cursor: tipoMenu === 'desplegable' ? 'pointer' : 'default',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  {section.title}
                  {tipoMenu === 'desplegable' && (
                    <span style={{ display: 'inline-flex' }}>
                      {seccionesAbiertas[section.title] ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
                    </span>
                  )}
                </div>
                {(tipoMenu === 'estatico' || seccionesAbiertas[section.title] || menuCollapsed) && (
                  section.items.map((item) => {
                    const ItemIcon = item.Icon;
                    return (
                      <button
                        key={item.id}
                        className={`menu-item ${isActive(item.path) ? 'active' : ''}`}
                        onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                      >
                        <span className="menu-icon"><ItemIcon className="w-4 h-4" /></span>
                        <span className="menu-label">{item.label}</span>
                      </button>
                    );
                  })
                )}
              </div>
            ))
          )}

          {permisosUsuario !== null && (
            <div
              className="sidebar-logout-wrap"
              style={{
                marginTop: '24px',
                paddingTop: '16px',
                paddingBottom: '8px',
                paddingLeft: '16px',
                paddingRight: '16px',
                borderTop: '1px solid var(--color-borde)',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <button
                onClick={handleLogout}
                className="sidebar-logout-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '10px 16px',
                  background: 'var(--color-peligro)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(220, 53, 69, 0.3)',
                  transition: 'background 0.15s, transform 0.1s, box-shadow 0.15s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--color-peligro-texto)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 53, 69, 0.45)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--color-peligro)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 53, 69, 0.3)';
                }}
                onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(1px)'; }}
                onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                <span className="logout-label">Cerrar Sesión</span>
              </button>
            </div>
          )}
        </div>

        <button 
          className="collapse-btn"
          onClick={() => setMenuCollapsed(!menuCollapsed)}
        >
          {menuCollapsed ? <ChevronDoubleRightIcon className="w-5 h-5" /> : <ChevronDoubleLeftIcon className="w-5 h-5" />}
        </button>
      </div>

      {/* Velo con blur detrás del menú lateral abierto en móvil (mismo look que un modal).
          Tocarlo cierra el menú. Solo visible en móvil (CSS). */}
      {mobileMenuOpen && (
        <div
          className="sidebar-mobile-overlay"
          onMouseDown={() => setMobileMenuOpen(false)}
        />
      )}

      <div className="main-content">
        <div className="top-bar">
          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <Bars3Icon className="w-6 h-6" />
          </button>

          {/* Barra de Accesos Rápidos (desktop) — al lado del menú, antes del perfil */}
          <div className="accesos-rapidos-desktop">
            {!accesosCargados ? null : accesosRapidosDesktop.length === 0 ? (
              <button
                className="ar-empty-btn"
                onClick={() => navigate('/accesos-rapidos')}
                title="Configurar accesos rápidos"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Agregá tus accesos directos aquí</span>
              </button>
            ) : (
              accesosRapidosDesktop.map(item => {
                const ItemIcon = item.Icon;
                return (
                  <button
                    key={item.id}
                    className={`ar-dock-item ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => navigate(item.path)}
                    title={item.label}
                  >
                    <span className="ar-dock-icon"><ItemIcon className="w-5 h-5" /></span>
                    <span className="ar-dock-label">{item.label}</span>
                  </button>
                );
              })
            )}
          </div>

          <div className="top-bar-right">
            {NIVELES_IA_DISPONIBLES.includes(nivelAsistenteIA) && (
              <button
                onClick={() => { setAsistenteMontado(true); setAsistenteAbierto(true); }}
                title="Lumi, tu guía del sistema"
                aria-label="Abrir Lumi"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'var(--color-primario)',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  flexShrink: 0,
                  marginRight: '10px'
                }}
              >
                <SparklesIcon className="w-5 h-5" />
              </button>
            )}
            <div className="mobile-user-info">
              {usuarioActual?.fotoPerfil ? (
                <img
                  src={usuarioActual.fotoPerfil}
                  alt="Foto de perfil"
                  style={{
                    width: '35px',
                    height: '35px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid var(--color-primario)'
                  }}
                />
              ) : (
                <div style={{
                  width: '35px',
                  height: '35px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-primario)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '18px'
                }}>
                  <UserCircleIcon className="w-5 h-5" />
                </div>
              )}
              <span style={{
                fontSize: '14px',
                fontWeight: 'bold',
                color: 'var(--color-texto)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '150px'
              }}>
                {usuarioActual?.nombre || 'Cargando...'}
              </span>
            </div>
            <div className="user-info">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {usuarioActual?.fotoPerfil ? (
                  <img
                    src={usuarioActual.fotoPerfil}
                    alt="Foto de perfil"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '2px solid var(--color-primario)'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-primario)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '20px'
                  }}>
                    <UserCircleIcon className="w-6 h-6" />
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--color-texto)' }}>
                    {usuarioActual?.nombre || 'Cargando...'}
                  </span>
                  <span className="current-date">{getCurrentDate()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="content-area">
    <LimiteErrorChunk>
    <Suspense fallback={<CargandoModulo />}>
    {candadoRuta === 'abrir' ? (
      <Outlet context={{ usuarioActual, permisosUsuario, puedeAbrirModulo }} />
    ) : candadoRuta === 'esperar' ? (
      <CargandoModulo />
    ) : (
      <SinAccesoModulo onIrAInicio={() => navigate('/', { replace: true })} />
    )}
    </Suspense>
    </LimiteErrorChunk>
  </div>

        {/* Bottom bar móvil de accesos rápidos — se oculta cuando el menú lateral está abierto */}
        {!mobileMenuOpen && (
          <div className="accesos-rapidos-mobile">
            {!accesosCargados ? null : accesosRapidosMobile.length === 0 ? (
              <button
                className="ar-mobile-empty"
                onClick={() => navigate('/accesos-rapidos')}
              >
                <PlusIcon className="w-5 h-5" />
                <span>Agregá tus accesos</span>
              </button>
            ) : (
              accesosRapidosMobile.map(item => {
                const ItemIcon = item.Icon;
                return (
                  <button
                    key={item.id}
                    className={`ar-mobile-item ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => navigate(item.path)}
                  >
                    <span className="ar-mobile-icon"><ItemIcon className="w-5 h-5" /></span>
                    <span className="ar-mobile-label">{item.label}</span>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
      <BurbujaNotificaciones puedeAbrirModulo={puedeAbrirModulo} />
      {/* Aviso de versión nueva del sistema (modal + botón flotante; nunca recarga solo) */}
      <AvisoActualizacion />

      {/* Lumi, el Asistente IA — panel de chat deslizable. Se monta al primer clic y queda montado (la charla se
          conserva al cerrar y abrir). Lo que sabe lo decide el servidor con los permisos de la persona. */}
      {asistenteMontado && (
        <LimiteErrorLumi onFallo={() => { setAsistenteAbierto(false); setAsistenteMontado(false); }}>
          <Suspense fallback={null}>
            <AsistenteIA
              abierto={asistenteAbierto}
              onClose={() => setAsistenteAbierto(false)}
              nivel={nivelAsistenteIA}
              userNombre={usuarioActual?.nombre}
              conversacionPedida={conversacionPedida}
            />
          </Suspense>
        </LimiteErrorLumi>
      )}

      {/* Aviso emergente de reposición de stock — a las personas elegidas en el módulo.
          Va con el <Modal> GLOBAL (no inline): de ahí salen la pantalla completa en
          móvil, la X que no se va con el scroll, el scroll interno y el botón atrás
          del teléfono. */}
      <Modal
        isOpen={!!alertaReposicion && alertaReposicion.length > 0}
        onClose={posponerReposicion}
        size="small"
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: 'var(--color-advertencia-texto)', display: 'inline-flex' }}>
              <ExclamationTriangleIcon className="w-5 h-5" />
            </span>
            Hay que volver a comprar
          </span>
        }
      >
        {alertaReposicion && (
          <>
            <p style={{ color: 'var(--color-texto-secundario)', fontSize: '14px', lineHeight: 1.6, margin: '0 0 14px' }}>
              {alertaReposicion.length === 1
                ? 'Un producto llegó a su límite de stock:'
                : `${alertaReposicion.length} productos llegaron a su límite de stock:`}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '18px' }}>
              {(expandidoReposicion ? alertaReposicion : alertaReposicion.slice(0, 6)).map((it) => (
                <div key={it.id} style={{
                  padding: '10px 12px', borderRadius: 'var(--radio-md, 8px)',
                  background: 'var(--color-card-elevado)', border: '1px solid var(--color-borde)'
                }}>
                  <strong style={{ fontSize: '14px', color: 'var(--color-texto)' }}>{it.productoNombre}</strong>
                  <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginTop: '3px' }}>
                    Quedan <strong style={{ color: 'var(--color-peligro-texto)' }}>{it.disponible}</strong> disponibles
                    {' '}(límite {it.limite}) · {it.bodegaNombre || 'Bodega'}
                  </div>
                </div>
              ))}

              {/* El resto se abre acá mismo, sin salir del aviso */}
              {alertaReposicion.length > 6 && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2px' }}>
                  <button
                    type="button"
                    className="btn-press"
                    onClick={() => setExpandidoReposicion((v) => !v)}
                    style={{
                      background: 'none', border: 'none', padding: '4px', cursor: 'pointer',
                      color: 'var(--color-primario-texto)', fontSize: '13px', fontWeight: 'bold',
                      display: 'inline-flex', alignItems: 'center', gap: '5px'
                    }}
                  >
                    {expandidoReposicion ? 'Ver menos' : `Ver los ${alertaReposicion.length - 6} restantes`}
                    <span style={{ display: 'inline-flex', transform: expandidoReposicion ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
                      <ChevronDownIcon className="w-4 h-4" />
                    </span>
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                className="btn-press hover-bright"
                onClick={posponerReposicion}
                style={{
                  flex: 1, minWidth: '150px', padding: '10px 18px', background: 'var(--color-neutro-solido)',
                  color: 'white', border: 'none', borderRadius: 'var(--radio-sm, 6px)', cursor: 'pointer',
                  fontSize: '14px', fontWeight: 600
                }}
              >
                Recordarme más tarde
              </button>
              {/* Solo a quien puede abrir el módulo (candado de ruta): a los demás el aviso les llega igual. */}
              {puedeAbrirModulo('alertas-reposicion') && (
              <button
                className="btn-press hover-bright"
                onClick={irAReposicion}
                style={{
                  flex: 1, minWidth: '150px', padding: '10px 18px', background: 'var(--color-primario)',
                  color: 'white', border: 'none', borderRadius: 'var(--radio-sm, 6px)', cursor: 'pointer',
                  fontSize: '14px', fontWeight: 600
                }}
              >
                Ver alertas
              </button>
              )}
            </div>
          </>
        )}
      </Modal>

      {/* Modal global de alerta del token Meta — aparece en cualquier módulo si ≤7 días al vencer */}
      {alertaTokenMeta && (() => {
        const dias = alertaTokenMeta.diasRestantes;
        const expirado = dias <= 0;
        const critico = !expirado && dias <= 3;
        const colorPrincipal = expirado ? '#dc3545' : critico ? '#e67e22' : '#f59e0b';
        const colorFondo = expirado ? '#fce8e8' : critico ? '#fdebd3' : '#fef5e1';
        return (
          <div
            onMouseDown={(e) => { if (e.target === e.currentTarget) cerrarAlertaTokenMeta(); }}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 99999, padding: '20px'
            }}
          >
            <div style={{
              background: 'white', borderRadius: '12px', maxWidth: '480px', width: '100%',
              padding: '24px', position: 'relative',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              borderTop: `6px solid ${colorPrincipal}`
            }}>
              {/* X estándar del sistema (pieza global de los modales) */}
              <button
                className="modal-close-std"
                onClick={cerrarAlertaTokenMeta}
                title="Cerrar"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>

              {/* Header con icono */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                <div style={{
                  width: '44px', height: '44px',
                  background: colorFondo, color: colorPrincipal,
                  borderRadius: '50%',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {expirado ? <ExclamationTriangleIcon className="w-6 h-6" /> : <KeyIcon className="w-6 h-6" />}
                </div>
                <h3 style={{ margin: 0, fontSize: '17px', color: '#333', fontWeight: 700 }}>
                  {alertaTokenMeta.titulo || 'Token Meta por vencer'}
                </h3>
              </div>

              {/* Mensaje */}
              <p style={{ color: '#555', fontSize: '14px', lineHeight: 1.6, marginBottom: '22px' }}>
                {alertaTokenMeta.mensaje}
              </p>

              {/* Botones */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button
                  onClick={cerrarAlertaTokenMeta}
                  style={{
                    padding: '10px 18px',
                    background: '#6c757d', color: 'white',
                    border: 'none', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '14px', fontWeight: 600
                  }}
                >
                  Cerrar
                </button>
                {/* Solo a quien puede abrir Ads Dashboard (candado de ruta). */}
                {puedeAbrirModulo('ads-dashboard') && (
                <button
                  onClick={irARenovarToken}
                  style={{
                    padding: '10px 22px',
                    background: 'var(--color-primario)', color: 'white',
                    border: 'none', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '14px', fontWeight: 600,
                    display: 'inline-flex', alignItems: 'center', gap: '6px'
                  }}
                >
                  <KeyIcon className="w-4 h-4" /> Ir a renovar
                </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default Layout;